/**
 * PromptRows - who is prompted for each person a call asks (v9.5.3).
 *
 * Under the Who list, one row per ticked character: a chip per account
 * that will be prompted (removable), a select to add one of the people at
 * the table, and the words "GM rolls" when nobody is. The rows start from
 * the defaults (engine/roster/actedAs promptDefaults: everyone who acted
 * as the character this session) and only a changed row is written into
 * the call as `prompt`.
 *
 * @param {object} props
 * @param {string[]} props.who - the ticked combatant ids, in order
 * @param {{id: string, name: string}[]} props.roster
 * @param {{uid: string, name: string}[]} props.people - the accounts at the table, the GM left out
 * @param {Record<string, string[]>} props.prompt - combatantId -> uids, as the form holds it
 * @param {(next: Record<string, string[]>) => void} props.onChange
 */
import common from '../../styles/common.module.css';
import styles from '../../styles/CallForTest.module.css';

function PromptRow({ combatant, uids, people, onChange }) {
  const nameOf = (uid) => people.find((p) => p.uid === uid)?.name ?? uid;
  const free = people.filter((p) => !uids.includes(p.uid));
  return (
    <div className={styles.callLine} data-testid={`call-prompt-${combatant.id}`}>
      <span>{combatant.name}:</span>
      {uids.length === 0 && (
        <span className={styles.caption} data-testid={`call-prompt-gm-${combatant.id}`}>
          GM rolls
        </span>
      )}
      {uids.map((uid) => (
        <button
          key={uid}
          type="button"
          className={common.btnSmall}
          data-testid={`call-prompt-${combatant.id}-${uid}`}
          title="Remove"
          onClick={() => onChange(uids.filter((u) => u !== uid))}
        >
          {nameOf(uid)} ✕
        </button>
      ))}
      {free.length > 0 && (
        <select
          className={styles.select}
          aria-label={`Also ask, for ${combatant.name}`}
          data-testid={`call-prompt-add-${combatant.id}`}
          value=""
          onChange={(e) => e.target.value && onChange([...uids, e.target.value])}
        >
          <option value="">Ask…</option>
          {free.map((p) => (
            <option key={p.uid} value={p.uid}>
              {p.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

export default function PromptRows({ who, roster, people, prompt, onChange }) {
  if (who.length === 0) return null;
  return (
    <div className={styles.field}>
      <span className={styles.caption}>Who rolls</span>
      {who.map((cid) => {
        const combatant = roster.find((c) => c.id === cid);
        if (!combatant) return null;
        return (
          <PromptRow
            key={cid}
            combatant={combatant}
            uids={prompt[cid] ?? []}
            people={people}
            onChange={(uids) => onChange({ ...prompt, [cid]: uids })}
          />
        );
      })}
    </div>
  );
}
